<!-- eslint-disable vue/no-unused-vars -->
<script setup lang="ts">
import AppState, { getNestedCustomValues } from '@/states/app'
import {
  CheckIcon,
  InfoCircleIcon,
  Trash01Icon,
  Trash03Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  AllExtensions,
  RequiredExtensions,
  UIRichTextEditor,
} from '@gohighlevel/ghl-text-editor'
import {
  UIButton,
  UIDivider,
  UIForm,
  UIFormItem,
  UIHeader,
  UIInput,
  UIInputGroup,
  UIInputGroupLabel,
  UIRadio,
  UISelect,
  UITooltip,
} from '@gohighlevel/ghl-ui'
import { History } from '@tiptap/extension-history'
import { Editor } from '@tiptap/vue-3'
import DOMPurify from 'dompurify'
import { debounce } from 'lodash-es'
import { computed, h, reactive, ref, watch, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'
import { CalendarService } from '../../class/services/CalendarService'
import {
  CalendarType,
  LocationConfigurations,
  LogoShape,
  TeamMember,
} from '../../models/calendar'
import {
  ALLOWED_ATTR_FOR_DESCRIPTION,
  EVENT_COLORS,
} from '../../utils/constants'
import {
  calendarNameFieldOptions,
  handleNestedCustomValueModification,
  meetingInviteTitleFieldOptionsEvent,
  meetingInviteTitleFieldOptionsV3,
  meetingLocationFieldOptionsEvent,
} from '../../utils/customValueMapper'
import { getTooltip } from '../../utils/generalFunctions'
import { hasDuplicateKeyValue, hasEmptyKeys } from '../../utils/validation'
import ImageUpload from '../common/ImageUpload.vue'
import TextEditorMenu from '../common/TextEditorMenu.vue'
import CustomVariablePickerV2 from '../dynamic/CustomVariablePickerV2.vue'
import TeamMemberPicker from './TeamMemberPicker.vue'

const teamMemberPicker = ref()
const isAliasFieldEnabled = ref<number[]>([])
const formRef = ref()
const { t } = useI18n()
const toast = useToast()

const meetingLocationNew = ref({
  custom: '',
  inbound_call: '{{location.phone}}',
  outbound_call: '{{location.phone}}',
  physical: '{{location.full_address}}',
})

const meetingLocationTypesEvent = computed(() => {
  const options = [
    {
      label: 'Phone',
      value: 'inbound_call',
    },
    {
      label: 'Full Address',
      value: 'physical',
    },
    {
      label: 'Custom',
      value: 'custom',
    },
    {
      label: 'Ask the Booker',
      value: 'booker',
    },
    {
      label: 'Zoom',
      value: 'zoom_conference',
      disabled: true,
    },
    {
      label: 'Google Meet',
      value: 'google_conference',
      disabled: true,
    },
    {
      label: 'Microsoft Teams',
      value: 'ms_teams_conference',
      disabled: true,
    },
  ]
  const enabledOptions = options.filter(option => !option.disabled)
  const disabledOptions = options.filter(option => option.disabled)

  if (disabledOptions.length > 0) {
    return [
      {
        type: 'group',
        label: `Supported Meeting Locations`,
        key: 'enabled-options',
        children: enabledOptions,
      },
      {
        type: 'group',
        label: t('calendar_advanced.meeting_details.not_supported_event'),
        key: 'disabled-options',
        children: disabledOptions,
      },
    ]
  }
  return options
})
const editor = ref<any>(null)

const state = reactive({
  isLoading: false,
  isSlugValidating: false,
  hasUserModifiedSlug: false,
  collectiveAddress: '' as string | undefined,
  selectedTeamMembers: [] as TeamMember[],
  formData: {
    name: '',
    eventColor: '#D50000',
    eventTitle: '',
    meetingLocation: '',
    locationConfigurations: [
      {
        kind: 'custom',
        location: '',
        position: 0,
        meetingId: 'custom_0',
      },
    ] as LocationConfigurations[],
    groupId: '',
    description: '',
    widgetSlug: '',
    fileList: [],
    widgetLogo: {
      shape: LogoShape.SQUARE,
      url: '',
    },
  },
  enableSameUserAssignment: false,
  enableSameUserAssignmentForReschedule: false,
})

const intializeEditor = () => {
  editor.value = new Editor({
    content: editingCalendar.value?.description || '',
    parseOptions: {
      preserveWhitespace: 'full',
    },
    extensions: [
      History,
      RequiredExtensions,
      AllExtensions.configure({
        placeholder: {
          placeholder: t('create_calendar.description_placeholder'),
          showForAllNodes: false,
        },
        divTag: {
          inline: false,
          preserveWhitespace: false,
          HTMLAttributes: {
            style: 'color:#10182899',
          },
        },
        preTag: {
          inline: false,
          preserveWhitespace: false,
          HTMLAttributes: {
            style: 'color:#10182899',
          },
        },
        textAlignment: { types: ['heading', 'paragraph'] },
        paragraph: {
          HTMLAttributes: {
            style: 'margin:0px;color:#10182899',
          },
        },
        heading: {
          HTMLAttributes: {
            style: 'color:#10182899',
          },
        },
        link: {
          HTMLAttributes: {
            target: '_blank',
          },
        },
      }),
    ],
    onUpdate({ editor }) {
      state.formData.description = editor.getHTML()
    },
  })
}

const groupsOptions = computed(() => {
  return (AppState.globalTeams as any)?.teams?.map((x: any) => {
    return {
      value: x.id,
      label: x.name,
    }
  })
})

const editingCalendar = computed(() => {
  return AppState.editingCalendar
})

const emit = defineEmits(['onSave', 'onCancel'])

const methods = {
  async loadData() {
    if (editingCalendar.value && !editor.value) {
      intializeEditor()
    }

    state.formData.name = editingCalendar.value?.name || ''
    state.formData.description = editingCalendar.value?.description || ''
    state.formData.groupId = editingCalendar.value?.groupId || ''
    state.formData.widgetSlug = editingCalendar.value?.widgetSlug || ''
    state.formData.eventTitle = editingCalendar.value?.eventTitle || ''
    state.formData.meetingLocation =
      editingCalendar.value?.meetingLocation || ''
    state.formData.locationConfigurations = editingCalendar.value
      ?.locationConfigurations?.length
      ? (editingCalendar.value
          ?.locationConfigurations as LocationConfigurations[])
      : [
          {
            kind: 'custom',
            location: editingCalendar.value?.meetingLocation || '',
            position: 0,
            meetingId: 'custom_0',
          },
        ]

    if (editingCalendar.value?.calendarType === CalendarType.COLLECTIVE) {
      // state.formData.meetingLocation =
      //   editingCalendar.value.teamMembers[0].meetingLocation
      state.selectedTeamMembers = editingCalendar.value?.teamMembers
      state.collectiveAddress = state.selectedTeamMembers.find(
        x => x.useMeetingForCollective
      )?.userId
    }

    state.formData.eventColor = editingCalendar.value?.eventColor || '#D50000'

    state.formData.widgetLogo.url = editingCalendar.value?.widgetLogo?.url || ''
    state.formData.widgetLogo.shape =
      editingCalendar.value?.widgetLogo?.shape || LogoShape.SQUARE
    state.enableSameUserAssignment =
      editingCalendar.value?.enableSameUserAssignment || false
    state.enableSameUserAssignmentForReschedule =
      editingCalendar.value?.enableSameUserAssignmentForReschedule || false
  },
  async handleUploadFromSystem(file: any) {
    console.log('🚀 ~ handleUploadFromSystem ~ file:', file)
    const formData = new FormData()
    formData.append('logo', file.file)
    const {
      data: { uploadedUrl },
    } = await CalendarService.uploadCalendarLogo(
      editingCalendar.value!.id,
      formData,
      editingCalendar?.value?.locationId || AppState.locationId
    )

    state.formData.widgetLogo.url = uploadedUrl
  },
  openMediaLibrary() {
    AppState.mediaLibraryFieldType = 'calendarLogo'
    AppState.parentApp?.then(parent => {
      parent.emit('media-library-open', {
        apiKey: '',
        tokenId: AppState.tokenId,
        companyId: AppState.companyId,
        altId: AppState.locationId,
        altType: 'location',
        hideHeader: true,
        allowedExtensions: ['image'],
        maxUploadSize: 50000000,
        category: 'calendars',
        subCategory: 'calendar_settings',
      })
      AppState.isMediaLibraryOpen = true
    })
  },
  async removeLogo() {
    state.formData.widgetLogo.url = ''
  },
  async emitSave() {
    if (
      editingCalendar.value?.calendarType === CalendarType.EVENT &&
      hasEmptyKeys(state.formData?.locationConfigurations)
    ) {
      toast.error('Please enter a value for all location types')
      return
    }
    if (
      editingCalendar.value?.calendarType === CalendarType.EVENT &&
      hasDuplicateKeyValue(state.formData?.locationConfigurations)
    ) {
      toast.error('Duplicate value for same location type is not allowed')
      return
    }
    try {
      await formRef.value.getForm().validate()
      isAliasFieldEnabled.value = []
    } catch (error) {
      return
    }
    const sanitizedDescription = DOMPurify.sanitize(
      state.formData.description,
      {
        ADD_ATTR: ALLOWED_ATTR_FOR_DESCRIPTION,
      }
    )
    const payload = {
      name: state.formData.name,
      description: sanitizedDescription,
      groupId: state.formData.groupId,
      widgetSlug: state.formData.widgetSlug,
      eventTitle: state.formData.eventTitle,
      eventColor: state.formData.eventColor,
      widgetLogo: state.formData.widgetLogo,
      enableSameUserAssignment:
        AppState.editCalendarValidationData.enableSameUserAssignment,
      enableSameUserAssignmentForReschedule:
        state.enableSameUserAssignmentForReschedule,
    } as Record<string, any>

    if (
      editingCalendar.value?.widgetLogo.url &&
      editingCalendar.value?.widgetLogo.url !== state.formData.widgetLogo.url
    ) {
      await CalendarService.removeCalendarLogo(
        editingCalendar.value!.id,
        AppState.locationId,
        { imageUrl: editingCalendar.value?.widgetLogo?.url }
      )
    }

    if (editingCalendar.value?.calendarType === CalendarType.EVENT) {
      payload.meetingLocation = ''
      payload.locationConfigurations =
        state.formData?.locationConfigurations.map(x => {
          return {
            kind: x?.kind,
            location: x?.location,
            position: x?.position,
            meetingId: x?.kind + '_' + x?.position,
            ...(x?.alias && { alias: x?.alias }),
          }
        })
    } else {
      payload.teamMembers =
        await teamMemberPicker.value.methods.getTeamMembers()
      if (!payload.teamMembers) {
        return null
      }

      if (editingCalendar.value?.calendarType === CalendarType.COLLECTIVE) {
        payload.teamMembers = payload.teamMembers.map((x: TeamMember) => {
          x.useMeetingForCollective = x.userId === state.collectiveAddress
          return x
        })
      }

      payload.eventType = teamMemberPicker.value.methods.getEventType()

      if (payload.teamMembers.length > 1) {
        payload.enableRecurring = false
      }
    }

    return payload
  },

  updateSelectedTeamMember(teamMembers) {
    state.selectedTeamMembers = teamMembers
    const selectedTeamMember = state.selectedTeamMembers.find(
      x => x.userId === state.collectiveAddress
    )
    state.collectiveAddress = selectedTeamMember ? state.collectiveAddress : ''
  },
  updateAdvancedSchedulingSettings(settings) {
    state.enableSameUserAssignment = settings.enableSameUserAssignment
    state.enableSameUserAssignmentForReschedule =
      settings.enableSameUserAssignmentForReschedule
  },
  openAliasField(index: number) {
    isAliasFieldEnabled.value.push(index)
  },
  closeAliasField(index: number) {
    isAliasFieldEnabled.value = isAliasFieldEnabled.value.filter(
      x => x !== index
    )
  },
  /**
   * Generate calendar slug based on calendar name
   */
  async generateSlugFromName(calendarName: string) {
    // Only generate slug if:
    // 1. Calendar name exists
    // 2. Either user hasn't manually modified the slug OR the slug is currently empty
    // 3. We're in create mode (not edit mode)
    if (
      !calendarName?.trim() ||
      (state.hasUserModifiedSlug && state.formData.widgetSlug?.trim()) ||
      !AppState.isNewCalendar || // Only for new calendars (create mode)
      editingCalendar.value?.id // Don't auto-generate if calendar already has an ID
    ) {
      return
    }

    state.isSlugValidating = true
    try {
      // Convert calendar name to slug format (lowercase, replace spaces with hyphens, remove special chars)
      let generatedSlug = calendarName
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
        .replace(/^-|-$/g, '') // Remove leading/trailing hyphens

      // Check if slug is available
      const { data } = await CalendarService.isValidCalendarSlug(
        generatedSlug,
        editingCalendar.value?.id
      )

      if (data?.available) {
        state.formData.widgetSlug = generatedSlug
      } else {
        // If slug is taken, append a number
        let counter = 1
        let slugWithNumber = `${generatedSlug}-${counter}`
        let isAvailable = false

        while (!isAvailable && counter < 100) {
          const { data: checkData } = await CalendarService.isValidCalendarSlug(
            slugWithNumber,
            editingCalendar.value?.id
          )
          if (checkData?.available) {
            state.formData.widgetSlug = slugWithNumber
            isAvailable = true
          } else {
            counter++
            slugWithNumber = `${generatedSlug}-${counter}`
          }
        }
      }
    } catch (error) {
      console.error('Error generating slug:', error)
    } finally {
      state.isSlugValidating = false
    }
  },
}

defineExpose({
  methods,
})
const rules = computed(() => {
  return {
    name: {
      validator() {
        if (!state.formData.name?.trim()) {
          return new Error('Please enter a name')
        }

        return true
      },
      message: t('calendar_advanced.meeting_details.enter_a_name'),
      trigger: 'blur',
    },
    eventTitle: {
      required: true,
      message: t('calendar_advanced.meeting_details.enter_an_event_title'),
      trigger: 'blur',
    },
    widgetSlug: {
      required: true,
      trigger: ['input', 'blur'],
      asyncValidator() {
        return new Promise(async (resolve, reject) => {
          state.isSlugValidating = true

          if (!state.formData.widgetSlug) {
            state.isSlugValidating = false
            return reject(new Error('Slug is required'))
          }

          const slugRegex = /^[a-z0-9-_]+(\/[a-z0-9-_]+)*$/

          if (!slugRegex.test(state.formData.widgetSlug)) {
            state.isSlugValidating = false
            return reject(new Error('This slug is not valid'))
          }

          try {
            const { data } = await CalendarService.isValidCalendarSlug(
              state.formData.widgetSlug,
              editingCalendar.value?.id
            )
            if (!data?.available) {
              return reject(new Error('This slug is already taken'))
            }
          } catch (error) {
            return reject(error)
          }

          state.isSlugValidating = false
          return resolve(true)
        })
      },
    },
  }
})

const MEETING_LOCATION_TYPES = ref({
  custom: t('calendar_advanced.meeting_details.custom'),
  zoom_conference: 'Zoom',
  ms_teams_conference: 'Microsoft Teams',
  google_conference: 'Google Meet',
  inbound_call: 'Phone',
  outbound_call: 'Phone Outgoing',
  physical: 'Full Address',
})

const collectiveMeetingLocations = computed(() => {
  return state.selectedTeamMembers?.map((t: any) => {
    const user = (AppState.globalUsers as any).users.find(
      (u: any) => u.id === t.userId
    )

    const locationConfig: LocationConfigurations = t
      ?.locationConfigurations?.[0] || {
      kind: 'custom',
      location: '',
      position: 0,
    }

    return {
      value: t.userId,
      label: `${user?.name}  (${
        MEETING_LOCATION_TYPES.value[locationConfig.kind] || 'Custom'
      }) `,
    }
  })
})

const calendarNameValuesRef = ref(calendarNameFieldOptions)
const meetingInviteTitleRef = ref(meetingInviteTitleFieldOptionsV3)
const meetingLocationEventRef = ref(meetingLocationFieldOptionsEvent)

// Function to load and update custom values with nested folder structure
const updateCustomValuesRefs = async () => {
  const meetingInviteTitleOptions = editingCalendar.value?.isEventCalendar
    ? meetingInviteTitleFieldOptionsEvent
    : meetingInviteTitleFieldOptionsV3

  // Set base options first (synchronous)
  calendarNameValuesRef.value = calendarNameFieldOptions
  meetingLocationEventRef.value = meetingLocationFieldOptionsEvent
  meetingInviteTitleRef.value = meetingInviteTitleOptions

  // Then fetch and add custom values (asynchronous)
  const nestedCustomValues = await getNestedCustomValues()
  const customValues = handleNestedCustomValueModification(nestedCustomValues)

  if (customValues) {
    calendarNameValuesRef.value = [...calendarNameFieldOptions, customValues]
    meetingLocationEventRef.value = [
      ...meetingLocationFieldOptionsEvent,
      customValues,
    ]
    meetingInviteTitleRef.value = [...meetingInviteTitleOptions, customValues]
  }
}

// Watch only when calendar ID changes to avoid unnecessary API calls
watch(
  () => editingCalendar.value?.id,
  newId => {
    if (newId) {
      updateCustomValuesRefs()
    }
  },
  { immediate: true }
)

watch(
  () => editingCalendar.value,
  () => {
    methods.loadData()
  },
  { immediate: true }
)

watchEffect(() => {
  AppState.isMultipleLocationEnabled =
    state.formData.locationConfigurations.length > 1
})

watch(
  () => AppState.mediaFileURI,
  () => {
    if (AppState.mediaFileURI) {
      if (AppState.mediaLibraryFieldType === 'calendarLogo') {
        state.formData.widgetLogo.url = AppState.mediaFileURI
        AppState.mediaLibraryFieldType = ''
        AppState.mediaFileURI = ''
      }
    }
  }
)

// Update calendar name in real-time as user types
watch(
  () => state.formData.name,
  newName => {
    if (editingCalendar.value) {
      editingCalendar.value.name = newName
    }
  }
)

// Create debounced function for generating slug
const debouncedGenerateSlug = debounce(methods.generateSlugFromName, 800)

// Watch for calendar name changes to auto-generate slug (only in create mode)
watch(
  () => state.formData.name,
  newName => {
    // Only trigger auto slug generation in create mode (not edit mode)
    if (
      newName?.trim() &&
      AppState.isNewCalendar &&
      !editingCalendar.value?.id
    ) {
      debouncedGenerateSlug(newName)
    }
  }
)

// Watch for manual slug modifications
watch(
  () => state.formData.widgetSlug,
  (newSlug, oldSlug) => {
    // Mark as user-modified if slug changes and it's not empty
    // (don't mark as modified when auto-generating from empty)
    if (newSlug !== oldSlug && oldSlug !== undefined && oldSlug !== '') {
      state.hasUserModifiedSlug = true
    }
  }
)

const isServiceCalendar = computed(() => {
  return editingCalendar.value?.isServiceCalendar
})

const insertMeetingInviteTitle = (value: string) => {
  state.formData.eventTitle = state.formData.eventTitle + ' ' + value
}

const insertCalendarName = (value: string) => {
  state.formData.name = state.formData.name + ' ' + value
}

const insertEventLocationConfig = (value: string, index: number) => {
  state.formData.locationConfigurations[index].location =
    state.formData.locationConfigurations[index].location + ' ' + value
}

const commonStyles = {
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
}

const renderLabel = (option, selected) => {
  return h(
    'div',
    {
      style: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        width: '100%',
        marginTop:
          option.type === 'group' && option.key === 'disabled-options'
            ? '5px'
            : '0',
      },
    },
    [
      option.type === 'group' && option.key === 'disabled-options'
        ? h('div', {
            style: {
              height: '1px',
              backgroundColor: 'var(--gray-300)',
              marginBottom: '8px',
              width: '100%',
              flexShrink: 0,
              alignSelf: 'stretch',
            },
          })
        : null,
      h(
        'div',
        {
          style: {
            ...commonStyles,
            width: '100%',
          },
          class: selected ? 'bg-primary' : '',
        },
        [
          h(
            'p',
            {
              style: {
                margin: 0,
                width: '100%',
              },
            },
            [option.label as string]
          ),
          option.description &&
            h(
              'span',
              {
                style: {
                  color: 'var(--gray-500)',
                },
              },
              option.description
            ),
        ]
      ),
    ]
  )
}
</script>

<template>
  <UIHeader
    id="text-only-header"
    :secondary-title="
      isServiceCalendar
        ? t('calendar_advanced.meeting_details.service_details')
        : t('calendar_advanced.meeting_details.meeting_details')
    "
    :description="
      t('calendar_advanced.meeting_details.meeting_details_description')
    "
  />

  <UIDivider />
  <UIForm
    id="create-calendar-form"
    ref="formRef"
    :model="state.formData"
    :rules="rules"
    class="w-full"
  >
    <UIFormItem
      :label="
        isServiceCalendar
          ? $t('create_calendar.service_logo')
          : $t('create_calendar.calendar_logo')
      "
      :tooltip="
        getTooltip(
          $t('calendar_advanced.meeting_details.calendar_logo_tooltip')
        )
      "
    >
      <div v-if="!state.formData.widgetLogo.url" class="w-full">
        <ImageUpload
          @uploadFromSystem="methods.handleUploadFromSystem"
          @chooseFromMediaLibrary="methods.openMediaLibrary"
        />
      </div>
      <div v-else class="flex items-start space-x-10">
        <img
          :src="state.formData.widgetLogo.url"
          class="h-40 w-36 object-fill"
          :class="{
            'rounded-full': state.formData.widgetLogo.shape === LogoShape.ROUND,
            'rounded-lg': state.formData.widgetLogo.shape === LogoShape.SQUARE,
          }"
          :alt="'create-logo'"
        />

        <div class="flex flex-col justify-start">
          <UIFormItem
            :label="$t('calendar_advanced.meeting_details.widget_shape')"
          >
            <div class="flex w-full flex-col space-y-4">
              <div class="flex space-x-4">
                <UIRadio
                  id="calendar-widget-logo-square"
                  :checked="
                    state.formData.widgetLogo.shape === LogoShape.SQUARE
                  "
                  :value="LogoShape.SQUARE"
                  :name="LogoShape.SQUARE"
                  @change="state.formData.widgetLogo.shape = LogoShape.SQUARE"
                >
                  {{ $t('calendar_advanced.meeting_details.square') }}
                </UIRadio>

                <UIRadio
                  id="calendar-widget-logo-round"
                  :checked="state.formData.widgetLogo.shape === LogoShape.ROUND"
                  :value="LogoShape.ROUND"
                  :name="LogoShape.ROUND"
                  @change="state.formData.widgetLogo.shape = LogoShape.ROUND"
                >
                  {{ $t('calendar_advanced.meeting_details.circle') }}
                </UIRadio>
              </div>
            </div>
          </UIFormItem>

          <UIButton
            id="calendar-logo-remove-btn"
            class="border-red-300 text-red-700"
            @click="methods.removeLogo"
          >
            <Trash01Icon class="mr-2 w-5" />
            <div class="">
              {{ $t('calendar_advanced.meeting_details.remove_logo') }}
            </div>
          </UIButton>
        </div>
      </div>
    </UIFormItem>
    <UIFormItem
      :label="
        isServiceCalendar
          ? $t('create_calendar.service_calendar_name')
          : $t('create_calendar.calendar_name')
      "
      :tooltip="
        getTooltip(
          $t('calendar_advanced.meeting_details.calendar_name_tooltip')
        )
      "
      path="name"
    >
      <UIInput
        id="calendar-name-input"
        v-model="state.formData.name"
        :placeholder="$t('create_calendar.eg_outbound_reach')"
      >
        <template #suffix>
          <CustomVariablePickerV2
            :dropdown-options="calendarNameValuesRef"
            @update:selected-value="val => insertCalendarName(val)"
          />
        </template>
      </UIInput>
    </UIFormItem>

    <UIFormItem
      :label="$t('create_calendar.description')"
      :tooltip="
        getTooltip($t('calendar_advanced.meeting_details.description_tooltip'))
      "
    >
      <div class="flex w-full flex-col">
        <TextEditorMenu :editor="editor" />
        <UIRichTextEditor
          :editor="editor"
          container-class="force-border-0 overflow-x-auto custom-editor-styling"
        />
      </div>
    </UIFormItem>
    <UIDivider />

    <UIFormItem
      :label="$t('create_calendar.group')"
      :tooltip="
        getTooltip($t('calendar_advanced.meeting_details.group_tooltip'))
      "
    >
      <UISelect
        id="calendar-group-select"
        v-model:value="state.formData.groupId"
        :options="groupsOptions"
        :filterable="true"
      />
    </UIFormItem>

    <UIFormItem
      :label="$t('create_calendar.custom_url')"
      path="widgetSlug"
      class="pb-2"
      :tooltip="
        getTooltip($t('calendar_advanced.meeting_details.custom_url_tooltip'))
      "
    >
      <UIInputGroup>
        <UIInputGroupLabel>{{
          $t('calendar_advanced.meeting_details.widget_bookings_path')
        }}</UIInputGroupLabel>
        <UIInput
          id="calendarUrl"
          :model-value="state.formData.widgetSlug"
          :placeholder="t('settings.calendar.my_calendar')"
          @update:model-value="state.formData.widgetSlug = $event.toLowerCase()"
        />
      </UIInputGroup>
    </UIFormItem>

    <UIFormItem
      :label="
        isServiceCalendar
          ? $t('create_calendar.appointment_invite_title')
          : $t('create_calendar.meeting_invite_title')
      "
      :tooltip="
        getTooltip(
          $t('calendar_advanced.meeting_details.meeting_invite_title_tooltip')
        )
      "
      path="eventTitle"
    >
      <UIInput
        id="calendar-event-title-input"
        v-model="state.formData.eventTitle"
        :placeholder="
          $t('calendar_advanced.meeting_details.contact_name_placeholder')
        "
      >
        <template #suffix>
          <CustomVariablePickerV2
            :dropdown-options="meetingInviteTitleRef"
            @update:selected-value="val => insertMeetingInviteTitle(val)"
          />
        </template>
      </UIInput>
    </UIFormItem>

    <UIFormItem
      v-if="editingCalendar?.calendarType === CalendarType.EVENT"
      :label="$t('calendar_advanced.meeting_details.meeting_location')"
      :tooltip="
        getTooltip(
          $t('calendar_advanced.meeting_details.meeting_location_tooltip')
        )
      "
    >
      <div class="w-full">
        <div
          v-for="(_, index) in state.formData.locationConfigurations"
          :key="index"
          class="mb-4 w-full gap-3"
        >
          <div class="flex w-full gap-3">
            <UISelect
              id="calendar-event-mutli-select"
              v-model:value="state.formData.locationConfigurations[index].kind"
              :options="meetingLocationTypesEvent"
              class="w-1/2 xl:w-1/3"
              :render-label="renderLabel"
              @change="(value: string) => {
    if (value === 'booker' && state.formData.locationConfigurations.findIndex(item => item.kind === 'booker') > -1) {
      state.formData.locationConfigurations.splice(index, 1)
      return toast.error($t('calendar_advanced.meeting_details.ask_the_booker_error_message'))
    }
    state.formData.locationConfigurations[index].location =
      meetingLocationNew[value]
  }
    "
            />
            <div class="flex flex-1 flex-col gap-3">
              <div class="flex flex-1 gap-3">
                <UIInput
                  v-if="
                    ['inbound_call', 'physical'].includes(
                      state.formData.locationConfigurations[index].kind
                    )
                  "
                  id="calendar-event-title-input"
                  v-model="
                    state.formData.locationConfigurations[index].location
                  "
                  :placeholder="
                    $t('calendar_advanced.meeting_details.enter_location')
                  "
                  class="flex-1"
                />
                <UIInputGroup
                  v-if="
                    state.formData.locationConfigurations[index].kind ===
                    'custom'
                  "
                  v-bind="{}"
                  style="max-width: 400px"
                  class="flex-1"
                >
                  <UIInput
                    v-model="
                      state.formData.locationConfigurations[index].location
                    "
                    :placeholder="
                      $t('calendar_advanced.meeting_details.enter_location')
                    "
                    class="flex-1"
                  >
                    <template #suffix>
                      <CustomVariablePickerV2
                        :dropdown-options="meetingLocationEventRef"
                        @update:selected-value="
                          val => insertEventLocationConfig(val, index)
                        "
                      />
                    </template>
                  </UIInput>
                  <UIButton
                    class="h-full"
                    :disabled="
                      isAliasFieldEnabled.includes(index) ||
                      state.formData.locationConfigurations[index].alias?.length
                    "
                  >
                    <div class="flex items-center gap-2">
                      <span
                        class="text-primary-600"
                        @click="
                          () => {
                            if (
                              !(
                                isAliasFieldEnabled.includes(index) ||
                                state.formData.locationConfigurations[index]
                                  .alias?.length
                              )
                            ) {
                              methods.openAliasField(index)
                            }
                          }
                        "
                      >
                        {{
                          $t(
                            'calendar_advanced.meeting_details.add_display_label'
                          )
                        }}
                      </span>
                      <UITooltip>
                        <template #trigger>
                          <InfoCircleIcon class="h-4 w-4 text-primary-600" />
                        </template>
                        <p>
                          {{
                            $t(
                              'calendar_advanced.meeting_details.add_display_label_tooltip'
                            )
                          }}
                        </p>
                      </UITooltip>
                    </div>
                  </UIButton>
                </UIInputGroup>
                <UIButton
                  id="remove-location"
                  class="h-10 w-10 rounded-lg bg-gray-100 text-black"
                  :text="true"
                  aria-labelledby="remove-location"
                  @click="
                    state.formData.locationConfigurations.splice(index, 1),
                      methods.closeAliasField(index)
                  "
                >
                  <Trash03Icon class="w-4" />
                </UIButton>
              </div>
              <div
                v-if="
                  isAliasFieldEnabled.includes(index) ||
                  state.formData.locationConfigurations[index].alias
                "
                class="flex flex-1 gap-3"
              >
                <UIInput
                  v-model="state.formData.locationConfigurations[index].alias"
                  :placeholder="
                    $t('calendar_advanced.meeting_details.enter_display_label')
                  "
                />
                <UIButton
                  id="remove-location"
                  class="h-10 w-10 rounded-lg bg-gray-100 text-black"
                  :text="true"
                  aria-labelledby="remove-location"
                  @click="
                    methods.closeAliasField(index),
                      (state.formData.locationConfigurations[index].alias = '')
                  "
                >
                  <Trash03Icon class="w-4" />
                </UIButton>
              </div>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <UIButton
            :id="'add-location-btn'"
            :type="'tertiary'"
            :size="'medium'"
            :disabled="false"
            :loading="false"
            :ghost="false"
            :quaternary="false"
            :circle="false"
            :text="true"
            :link-gray="undefined"
            :style="{ backgroundColor: '#EFF4FF' }"
            class="rounded px-4 py-3"
            @click="
              state.formData.locationConfigurations.push({
                kind: 'custom',
                location: '',
                position: state.formData.locationConfigurations.length,
                meetingId:
                  'custom' + '_' + state.formData.locationConfigurations.length,
              })
            "
          >
            {{ $t('calendar_advanced.meeting_details.add_location') }}
          </UIButton>
          <UITooltip>
            <template #trigger>
              <InfoCircleIcon class="h-4 w-4 text-primary-600" />
            </template>
            <p>
              {{
                $t(
                  'calendar_advanced.meeting_details.multiple_location_support_message'
                )
              }}
            </p>
          </UITooltip>
        </div>
      </div>
    </UIFormItem>

    <TeamMemberPicker
      ref="teamMemberPicker"
      @on-team-member-select="methods.updateSelectedTeamMember"
      @on-advanced-scheduling-settings-update="
        methods.updateAdvancedSchedulingSettings
      "
    />
    <UIFormItem
      v-if="editingCalendar?.calendarType === CalendarType.COLLECTIVE"
      class="mt-[20px]"
      :label="$t('calendar_advanced.meeting_details.meeting_location')"
      :tooltip="
        getTooltip(
          $t('calendar_advanced.meeting_details.meeting_location_tooltip')
        )
      "
    >
      <UISelect
        id="calendar-collective-address-select"
        v-model:value="state.collectiveAddress"
        :options="collectiveMeetingLocations"
      />
    </UIFormItem>

    <UIFormItem
      class="mt-4"
      :label="$t('create_calendar.meeting_color')"
      :tooltip="
        getTooltip(
          $t('calendar_advanced.meeting_details.meeting_color_tooltip')
        )
      "
    >
      <div
        v-for="color in EVENT_COLORS"
        :key="color.colorName"
        class="mr-1 flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg border"
        :style="{
          'background-color': color.colorHexCode,
        }"
        role="button"
        :aria-labelledby="'event-color-' + color.colorName"
        :title="color.colorName"
        :aria-label="color.colorHexCode"
        @click="state.formData.eventColor = color.colorHexCode"
        @keypress="state.formData.eventColor = color.colorHexCode"
      >
        <CheckIcon
          v-if="state.formData.eventColor === color.colorHexCode"
          class="h-6 w-6 text-white"
        />
      </div>
    </UIFormItem>
  </UIForm>
</template>

<style lang="scss">
.ui-upload-container .upload-icon svg {
  padding: 0px !important;
}

#create-calendar-form {
  .force-border-0 {
    border: 1px solid #d0d5dd !important;
    padding: 15px !important;
    max-height: 200px !important;
    min-height: 100px !important;
  }

  .ProseMirror p {
    margin: 0;
    font-size: 13px !important;
  }

  .ProseMirror ol {
    font-size: 14px !important;
  }

  .v3-search input::placeholder {
    font-size: 1rem;
    text-align: left;
    font-weight: normal;
    font-size: 0.875rem;
  }
}
</style>
